// frontend/src/api/orgs.js
import client from "./client";

/**
 * Organisation API — thin wrappers around the /api/orgs endpoints.
 */

/**
 * List every organisation the current user belongs to (for the org switcher).
 * @returns {Promise<Array<{org_id: string, name: string, slug: string, role: string, white_account: boolean}>>}
 */
export async function listOrgs() {
  const response = await client.get("/api/orgs");
  return response.data;
}

/**
 * Fetch the active organisation's details plus the caller's role in it.
 * @returns {Promise<{id: string, name: string, slug: string, white_account: boolean, created_at: string, role: string}>}
 */
export async function getCurrentOrg() {
  const response = await client.get("/api/orgs/me");
  return response.data;
}

/**
 * Fetch the active org's plan, current-month usage, and usage percentages.
 * @returns {Promise<{
 *   plan: {name: string, limits: object, stripe_price_id: string|null},
 *   usage: {codes_created: number, scans_recorded: number, api_calls: number},
 *   percentages: {codes_pct: number, scans_pct: number, api_calls_pct: number},
 *   limits: object
 * }>}
 */
export async function getUsage() {
  const response = await client.get("/api/orgs/me/usage");
  return response.data;
}

/**
 * Update the active org's name and/or slug (admin+). Returns the updated org.
 * @param {{name?: string, slug?: string}} fields
 */
export async function updateOrg(fields) {
  const response = await client.put("/api/orgs/me", fields);
  return response.data;
}

/**
 * List the active org's members.
 * @returns {Promise<Array<{user_id: number, email: string, display_name: string|null, role: string, joined_at: string}>>}
 */
export async function getMembers() {
  const response = await client.get("/api/orgs/me/members");
  return response.data;
}

/**
 * Remove a member from the active org (admin+).
 * @param {number} userId
 */
export async function removeMember(userId) {
  await client.delete(`/api/orgs/me/members/${userId}`);
}

/**
 * Change a member's role (owner only). Returns the updated member.
 * @param {number} userId
 * @param {string} role  one of owner|admin|member
 */
export async function changeMemberRole(userId, role) {
  const response = await client.patch(
    `/api/orgs/me/members/${userId}/role`,
    { role }
  );
  return response.data;
}

/**
 * List the active org's pending invitations (admin+).
 * @returns {Promise<Array<{id: string, invited_email: string, role: string, expires_at: string, created_at: string}>>}
 */
export async function listInvitations() {
  const response = await client.get("/api/orgs/me/invitations");
  return response.data;
}

/**
 * Invite an email to the active org (admin+). Returns the created invitation.
 * @param {string} email
 * @param {string} role  one of owner|admin|member
 */
export async function createInvitation(email, role) {
  const response = await client.post("/api/orgs/me/invitations", {
    email,
    role,
  });
  return response.data;
}

/**
 * Revoke a pending invitation (admin+).
 * @param {string} invitationId
 */
export async function revokeInvitation(invitationId) {
  await client.delete(`/api/orgs/me/invitations/${invitationId}`);
}
